'use client';

import {
  hashDiagnosticEntity,
  setWalletAssetEntity,
  useAuth,
  useGetProfilePortfolioSnapshot,
  useQueries,
  useQuery,
  useQueryClient,
  type WalletAssetEntity,
  walletAssetEntityObserverOptions,
} from '@morfi/backend-sdk-v2/client';
import type { FundingWalletRefresh } from '@morfi/onramp-sdk/react';
import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import { PublicKey } from '@solana/web3.js';
import { D6_SCALE, getUsdcMint } from '@/config/solana';
import type { Network } from '@/lib/network/types';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import { parseTokenAccountBalance } from '@/lib/solana/ata';
import { reportError } from '@/lib/telemetry/client';
import { logger } from '@/lib/telemetry/logger';
import { getConnection } from '@/lib/tx-engine/connection';

export const WALLET_BALANCE_QUERY_KEY = 'wallet-balance';

interface WalletBalanceRequestMetadata {
  readonly addresses: readonly string[];
  readonly fetchedAtMs: number;
  readonly slot: string;
}

interface FetchedWalletBalances extends WalletBalanceRequestMetadata {
  readonly entities: readonly WalletAssetEntity[];
}

async function fetchBatchBalances(
  addresses: readonly string[],
  network: Network,
): Promise<FetchedWalletBalances> {
  const connection = getConnection(network);
  const mint = getUsdcMint(network);
  const usdcMint = new PublicKey(mint);
  const fetchedAtMs = Date.now();
  const rawBalances = new Map<string, string>();
  const ataKeys: PublicKey[] = [];
  const validAddresses: string[] = [];

  for (const address of addresses) {
    try {
      const walletPubkey = new PublicKey(address);
      ataKeys.push(getAssociatedTokenAddressSync(usdcMint, walletPubkey));
      validAddresses.push(address);
    } catch (error: unknown) {
      reportError(error, {
        component: 'fetchBatchBalances',
        network,
        walletHash: hashDiagnosticEntity(address),
        mintHash: hashDiagnosticEntity(mint),
      });
      rawBalances.set(address, '0');
    }
  }

  const response =
    ataKeys.length === 0
      ? { context: { slot: 0 }, value: [] }
      : await connection.getMultipleAccountsInfoAndContext(ataKeys);
  for (let index = 0; index < validAddresses.length; index += 1) {
    const address = validAddresses[index];
    if (address === undefined) continue;
    const account = response.value[index];
    rawBalances.set(
      address,
      account?.data === undefined ? '0' : parseTokenAccountBalance(account.data).toString(),
    );
  }

  const slot = response.context.slot.toString();
  return {
    addresses,
    fetchedAtMs,
    slot,
    entities: addresses.map(
      (wallet): WalletAssetEntity => ({
        network,
        wallet,
        mint,
        rawBalance: rawBalances.get(wallet) ?? '0',
        fetchedAtMs,
        slot,
        source: 'rpc',
      }),
    ),
  };
}

export interface WalletBalancesResult {
  /** Per-wallet display-dollar balances, keyed by address. */
  perWallet: Map<string, number>;
  /** Per-wallet raw u64 D6 balances, keyed by address. */
  perWalletRaw: Map<string, bigint>;
  /** Sum of all per-wallet balances in display dollars. */
  total: number;
  /** True while the batched fetch is in progress for any address. */
  isLoading: boolean;
  /** True if the fetch errored. */
  isError: boolean;
}

export function useInvalidateWalletBalances(): (walletAddress?: string) => void {
  const queryClient = useQueryClient();
  const network = useActiveNetwork();
  return (walletAddress?: string): void => {
    if (walletAddress === undefined) {
      void queryClient.invalidateQueries({ queryKey: [WALLET_BALANCE_QUERY_KEY] });
      return;
    }

    const batchQueries = queryClient.getQueryCache().findAll({
      queryKey: [WALLET_BALANCE_QUERY_KEY, 'batch', network],
    });
    for (const query of batchQueries) {
      const addressSet = query.queryKey[3];
      if (typeof addressSet === 'string' && addressSet.split(',').includes(walletAddress)) {
        // ALLOW: this hook owns the frontend Solana RPC batch query index.
        // REASON: the SDK owns normalized wallet assets, but it cannot refetch this RPC-only source.
        void queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true }); // ALLOW-STATE-OWNERSHIP: exact RPC query index
      }
    }
  };
}

export function useWalletBalances(addresses: string[]): WalletBalancesResult {
  const queryClient = useQueryClient();
  const network = useActiveNetwork();
  const mint = getUsdcMint(network);
  const normalizedAddresses = [...new Set(addresses)].sort();
  const request = useQuery<WalletBalanceRequestMetadata, Error>({
    queryKey: [WALLET_BALANCE_QUERY_KEY, 'batch', network, normalizedAddresses.join(',')],
    queryFn: async (): Promise<WalletBalanceRequestMetadata> => {
      try {
        const fetched = await fetchBatchBalances(normalizedAddresses, network);
        for (const entity of fetched.entities) {
          setWalletAssetEntity(queryClient, entity, logger);
        }
        return {
          addresses: fetched.addresses,
          fetchedAtMs: fetched.fetchedAtMs,
          slot: fetched.slot,
        };
      } catch (error: unknown) {
        reportError(error, {
          component: 'useWalletBalances',
          network,
          walletSetHash: hashDiagnosticEntity(normalizedAddresses.join(',')),
          mintHash: hashDiagnosticEntity(mint),
        });
        throw error;
      }
    },
    enabled: normalizedAddresses.length > 0,
    staleTime: 30_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  });
  const entityQueries = useQueries({
    queries: normalizedAddresses.map((wallet) =>
      walletAssetEntityObserverOptions(queryClient, { network, wallet, mint }),
    ),
  });

  const perWallet = new Map<string, number>();
  const perWalletRaw = new Map<string, bigint>();
  let total = 0;
  for (let index = 0; index < normalizedAddresses.length; index += 1) {
    const address = normalizedAddresses[index];
    const entity = entityQueries[index]?.data as WalletAssetEntity | undefined;
    if (address === undefined || entity === undefined) continue;
    const rawBalance = BigInt(entity.rawBalance);
    const displayBalance = Number(rawBalance) / D6_SCALE;
    perWallet.set(address, displayBalance);
    perWalletRaw.set(address, rawBalance);
    total += displayBalance;
  }

  return {
    perWallet,
    perWalletRaw,
    total,
    isLoading: normalizedAddresses.length === 0 ? false : request.isLoading,
    isError: normalizedAddresses.length === 0 ? false : request.isError,
  };
}

/** Refresh display authority, not an inferred balance delta from a payment callback. */
export function useRefreshPortfolioWallet(): FundingWalletRefresh {
  const { user, isAuthenticated } = useAuth();
  // ALLOW-UNSCOPED: the canonical portfolio snapshot is a strict, bounded DTO.
  const snapshot = useGetProfilePortfolioSnapshot({ userId: user?.id ?? '' }, { enabled: false });
  const mint = getUsdcMint(useActiveNetwork());
  return async ({
    destination,
    mint: outcomeMint,
    signal,
  }: Parameters<FundingWalletRefresh>[0]): ReturnType<FundingWalletRefresh> => {
    signal.throwIfAborted();
    if (!isAuthenticated || !user) throw new Error('onramp_wallet_refresh_unauthenticated');
    const result = await snapshot.refetch();
    signal.throwIfAborted();
    const observed = outcomeMint
      ? result?.positions.find(
          (row) => row.wallet === destination && row.outcomeMint === outcomeMint,
        )
      : result?.quoteBalances.find((row) => row.wallet === destination && row.quoteMint === mint);
    if (!result?.wallets.includes(destination) || !observed) {
      logger.warn('onramp.wallet_refresh_failed', {
        reason: !result ? 'snapshot_unavailable' : 'destination_unobserved',
      });
      throw new Error('onramp_wallet_refresh_unavailable');
    }
    return { destination, observedAtMs: Date.now() };
  };
}
